import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { Fragment, lazy, Suspense, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { Checkbox } from '@atlaskit/checkbox';
import { FlagGroup } from '@atlaskit/flag';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { deleteSessionItem, getSessionItem, setSessionItem } from '@atlassian/search-dialog';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onClickDisableDefinition } from '../analytics';
import { DefinitionUpdateConfirmationFlag } from '../curated/edit-definition-modal/definition-update-confirmation-flag';
import { EditDefinitionModal } from '../curated/edit-definition-modal/edit-definition-modal';
import { DefinitionEditHistory } from '../curated/edit-history-modal/edit-history-modal';
import { toggleDefinitionErrorPayload } from '../error-analytics';
import { FeedbackButtons } from './feedback-buttons';
import { FeedbackModal } from './feedback-modal';
import { FeedbackOptionsModal } from './feedback-options-modal';
import { AIDefinitionFooterSection, EditedDefinitionFooterSection } from './footer-section';
import { messages } from './messages';
import { useToggleDefinitionSupplier } from './useToggleDefinitionSupplier';
var AkFeedbackFlag = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */'@atlaskit/feedback-collector').then(function (_ref) {
    var FeedbackFlag = _ref.FeedbackFlag;
    return {
      default: FeedbackFlag
    };
  });
});
var FeedbackButtonWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 'space.050',
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  color: 'color.text.accent.gray',
  paddingLeft: 'space.300',
  paddingRight: 'space.300'
});
var AI_DEFINITION_TOGGLE_CACHE_KEY = 'atlassian.search-ai.ai-definition-toggle';
export var DefinitionFooter = function DefinitionFooter() {
  var _useAIAnswerContext = useAIAnswerContext(),
    answerEditor = _useAIAnswerContext.answerEditor,
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    readingAidsOptions = _useAIAnswerContext.readingAidsOptions,
    isAnswered = _useAIAnswerContext.isAnswered,
    hasError = _useAIAnswerContext.hasError,
    commonAttributes = _useAIAnswerContext.commonAttributes,
    definitionToggleEnabled = _useAIAnswerContext.definitionToggleEnabled;
  var keyPhrase = commonAttributes.query,
    workspaceId = commonAttributes.workspaceId;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage,
    locale = _useIntl.locale;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showFeedbackOptionsModal = _useState2[0],
    setShowFeedbackOptionsModal = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    showEditDefinitionModal = _useState4[0],
    setShowEditDefinitionModal = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showFeedbackModal = _useState6[0],
    setShowFeedbackModal = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    showEditHistoryModal = _useState8[0],
    setShowEditHistoryModal = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    displayFlag = _useState10[0],
    setDisplayFlag = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    displaySuccessFlag = _useState12[0],
    setDisplaySuccessFlag = _useState12[1];
  var cachedTermDefinitionDisabled = JSON.parse(getSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY) || '{}');
  var _useState13 = useState(cachedTermDefinitionDisabled.keyPhrase === keyPhrase),
    _useState14 = _slicedToArray(_useState13, 2),
    termDefinitionDisabled = _useState14[0],
    setTermDefinitionDisabled = _useState14[1];
  var _ref2 = readingAidsOptions || {},
    definitionCurationOptions = _ref2.definitionCurationOptions;
  var editDefinitionEnabled = !!definitionCurationOptions;
  var _useToggleDefinitionS = useToggleDefinitionSupplier(),
    toggleDefinition = _useToggleDefinitionS.toggleDefinition;
  var onToggleDefinitionDisable = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;
            setTermDefinitionDisabled(function (current) {
              return !current;
            });
            _context.next = 4;
            return toggleDefinition({
              workspaceId: workspaceId,
              keyPhrase: keyPhrase,
              isDisabled: !termDefinitionDisabled
            });
          case 4:
            if (termDefinitionDisabled) {
              deleteSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY);
            } else {
              fireAnalyticsEvent(onClickDisableDefinition(commonAttributes));
              setSessionItem(AI_DEFINITION_TOGGLE_CACHE_KEY, JSON.stringify({
                keyPhrase: keyPhrase
              }));
            }
            _context.next = 11;
            break;
          case 7:
            _context.prev = 7;
            _context.t0 = _context["catch"](0);
            setTermDefinitionDisabled(function (current) {
              return !current;
            });
            fireAnalyticsEvent(toggleDefinitionErrorPayload(_context.t0, commonAttributes));
          case 11:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[0, 7]]);
    }));
    return function onToggleDefinitionDisable() {
      return _ref3.apply(this, arguments);
    };
  }();
  return /*#__PURE__*/React.createElement(Fragment, null, !hasError && isAnswered && /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between",
    xcss: FeedbackButtonWrapperStyles
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(FeedbackButtons, {
    setShowFeedbackOptionsModal: setShowFeedbackOptionsModal,
    setDisplayFlag: setDisplayFlag
  })), definitionToggleEnabled && fg('kd_definitions_toggle_definition') && /*#__PURE__*/React.createElement(Checkbox, {
    isChecked: termDefinitionDisabled,
    label: messages.definition_footer_hide_term_definition_message.defaultMessage,
    value: messages.definition_footer_hide_term_definition_message.defaultMessage,
    onChange: onToggleDefinitionDisable
  })), answerEditor ? /*#__PURE__*/React.createElement(EditedDefinitionFooterSection, {
    setShowEditHistoryModal: setShowEditHistoryModal
  }) : /*#__PURE__*/React.createElement(AIDefinitionFooterSection, null), /*#__PURE__*/React.createElement(ModalTransition, null, showEditHistoryModal && editDefinitionEnabled && /*#__PURE__*/React.createElement(DefinitionEditHistory, {
    curatedDefinitionScopeConfig: definitionCurationOptions.curatedDefinitionScopeConfig,
    onClose: function onClose() {
      return setShowEditHistoryModal(false);
    }
  }), showFeedbackOptionsModal && /*#__PURE__*/React.createElement(FeedbackOptionsModal, {
    setShowFeedbackOptionsModal: setShowFeedbackOptionsModal,
    setShowEditDefinitionModal: setShowEditDefinitionModal,
    setShowFeedbackModal: setShowFeedbackModal,
    editDefinitionEnabled: editDefinitionEnabled
  }), showFeedbackModal && /*#__PURE__*/React.createElement(FeedbackModal, {
    setFeedbackModal: setShowFeedbackModal,
    isCuratedDefinition: !!answerEditor,
    setDisplayFlag: setDisplayFlag
  }), showEditDefinitionModal && editDefinitionEnabled && /*#__PURE__*/React.createElement(EditDefinitionModal, {
    curatedDefinitionScopeConfig: definitionCurationOptions.curatedDefinitionScopeConfig,
    closeEditModal: function closeEditModal() {
      return setShowEditDefinitionModal(false);
    },
    openFeedbackOptionsModal: function openFeedbackOptionsModal() {
      return setShowFeedbackOptionsModal(true);
    },
    onSaveEditModal: function onSaveEditModal() {
      return setDisplaySuccessFlag(true);
    }
  })), displayFlag && /*#__PURE__*/React.createElement(FlagGroup, {
    onDismissed: function onDismissed() {
      return setDisplayFlag(false);
    }
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(AkFeedbackFlag, {
    locale: locale,
    description: formatMessage(messages.definition_footer_feedback_flag_description)
  }))), displaySuccessFlag && /*#__PURE__*/React.createElement(DefinitionUpdateConfirmationFlag, {
    onDismissed: function onDismissed() {
      return setDisplaySuccessFlag(false);
    }
  }));
};